<template>
  <div>
    <div v-if="loading" class="dw-loading">
      <b-spinner small></b-spinner> Loading...
    </div>
    <div v-else-if="!glance" class="dw-empty">No activity data available for your account.</div>
    <template v-else>
      <div class="dw-glance-row">
        <div class="dw-glance-tile">
          <div class="dw-stat-tile-label">{{ windowLabel }}</div>
          <div class="dw-glance-value">{{ trips.toLocaleString() }}</div>
          <div class="dw-glance-note">
            <span class="dw-glance-strong">{{ glance.out_now.toLocaleString() }}</span> out now
          </div>
        </div>

        <div class="dw-glance-tile">
          <div class="dw-stat-tile-label">Drivers Utilized</div>
          <div class="dw-glance-value">
            {{ drivers.count.toLocaleString() }}
            <span v-if="drivers.fleet_total" class="dw-glance-of">of {{ drivers.fleet_total.toLocaleString() }}</span>
          </div>
          <div class="dw-glance-note">{{ utilizationNote(drivers, 'driver') }}</div>
        </div>

        <div class="dw-glance-tile">
          <div class="dw-stat-tile-label">Vehicles Utilized</div>
          <div class="dw-glance-value">
            {{ vehicles.count.toLocaleString() }}
            <span v-if="vehicles.fleet_total" class="dw-glance-of">of {{ vehicles.fleet_total.toLocaleString() }}</span>
          </div>
          <div class="dw-glance-note">{{ utilizationNote(vehicles, 'vehicle') }}</div>
        </div>
      </div>

      <div class="dw-glance-chart">
        <div class="dw-section-heading">{{ chartHeading }}</div>
        <div v-if="!windowDays.length" class="dw-empty">No departures scheduled in this period.</div>
        <div
          v-else
          class="dw-vbars"
          :class="{ 'dw-vbars--dense': isDense, 'dw-vbars--packed': isPacked }"
          role="img"
          :aria-label="windowAriaLabel"
        >
          <div
            v-for="day in windowDays"
            :key="day.date"
            class="dw-vbar-col"
            :class="{ 'dw-vbar-col--today': day.is_today }"
            :title="day.label + ' ' + day.date + ': ' + day.count + (day.count === 1 ? ' departure' : ' departures')"
          >
            <div v-if="!isPacked" class="dw-vbar-count">{{ day.count.toLocaleString() }}</div>
            <div class="dw-vbar-track">
              <div class="dw-vbar-fill" :style="{ height: day.pct + '%' }"></div>
            </div>
            <div class="dw-vbar-label">{{ day.show_label ? day.label : '' }}</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { get_tenant_glance } from '../../networking/apis'
import { get_tbn_tenant_glance } from '../../networking/tbn_apis'

const EMPTY_UTILIZATION = { count: 0, fleet_total: null, pct: null }

export default {
  name: 'widget_at_a_glance',
  props: {
    tenant: { type: String, required: true },
    isTbn: { type: Boolean, default: false },
    // the dashboard's range selection. every figure below covers it, "out
    // now" excepted -- a count of what is on the road right now has no
    // windowed reading, so it stays real-time whatever the range says.
    daysRange: { type: Number, default: 7 },
  },
  data() {
    return {
      loading: true,
      glance: null,
    }
  },
  computed: {
    // the window the api actually answered for, which is what the tiles and
    // the heading must describe -- not daysRange, which may have changed a
    // moment ago and be one in-flight request ahead of what is on screen
    windowInfo() {
      return (this.glance && this.glance.window) || {}
    },
    windowDayCount() {
      return this.windowInfo.days_count || this.daysRange
    },
    windowLabel() {
      return this.windowDayCount === 1 ? 'Trips Today' : 'Trips, Last ' + this.windowDayCount + ' Days'
    },
    chartHeading() {
      return this.windowDayCount === 1 ? 'Departures today' : 'Departures, last ' + this.windowDayCount + ' days'
    },
    chartedDayCount() {
      return (this.windowInfo.days || []).length
    },
    // past a week the columns are too narrow for the default type, and past a
    // fortnight they are too narrow for a number above every bar. no day is
    // ever dropped -- the chart spans the same window the tiles report, and
    // every count stays reachable through the column title and the aria label.
    isDense() {
      return this.chartedDayCount > 7
    },
    isPacked() {
      return this.chartedDayCount > 14
    },
    // one label per ~10 columns once they are packed, so the dates read as an
    // axis rather than a smear. counted back from today, which keeps its own.
    labelStride() {
      return this.isPacked ? Math.ceil(this.chartedDayCount / 10) : 1
    },
    trips() {
      // trips_today is the pre-window field, kept as the fallback so a cached
      // response from an older api still renders a number rather than a dash
      const glance = this.glance || {}
      return (glance.trips === undefined || glance.trips === null) ? (glance.trips_today || 0) : glance.trips
    },
    // distinct drivers/vehicles on trips across the window, against the fleet
    // totals the nightly stats job writes to the tenant record. fleet_total is
    // null until that job has run, and the denominator simply drops out then.
    drivers() {
      return (this.glance && this.glance.drivers_utilized) || EMPTY_UTILIZATION
    },
    vehicles() {
      return (this.glance && this.glance.vehicles_utilized) || EMPTY_UTILIZATION
    },
    // heights are shares of the busiest day, so the shape of the window reads
    // at a glance; the count above each bar carries the actual value
    windowDays() {
      const days = this.windowInfo.days || []
      const max = Math.max(1, ...days.map(day => day.count || 0))
      const stride = this.labelStride
      return days.map((day, i) => ({
        ...day,
        count: day.count || 0,
        // a zero must draw no bar at all, or it reads as a tiny nonzero
        pct: day.count ? Math.max(6, (day.count / max) * 100) : 0,
        show_label: (days.length - 1 - i) % stride === 0,
      }))
    },
    windowAriaLabel() {
      return this.chartHeading + ': ' +
        this.windowDays.map(day => day.label + ' ' + day.count).join(', ')
    },
  },
  created() {
    this.load()
  },
  methods: {
    utilizationNote(stat, noun) {
      const period = this.windowDayCount === 1 ? 'today' : 'in the last ' + this.windowDayCount + ' days'
      if (!stat.count) return this.windowDayCount === 1 ? 'None assigned today' : 'None assigned ' + period
      if (stat.pct === null || stat.pct === undefined) {
        return 'On trips ' + period
      }
      // a big enough fleet rounds a real count to 0% -- saying "0%" next to a
      // nonzero count reads as a contradiction, so floor the wording instead
      const share = stat.pct === 0 ? 'under 1%' : stat.pct + '%'
      return share + ' of the ' + noun + ' fleet ' + period
    },
    async load() {
      this.loading = true
      try {
        const fetch = this.isTbn
          ? get_tbn_tenant_glance(this.tenant, this.daysRange)
          : get_tenant_glance(this.tenant, this.daysRange)
        const { value: { data = {} } } = await fetch
        this.glance = data && data.window ? data : null
      } catch (e) {
        this.glance = null
      }
      this.loading = false
    },
  },
  watch: {
    tenant() {
      this.load()
    },
    daysRange() {
      this.load()
    },
  },
}
</script>
